import {Component, HostBinding, Input, OnDestroy, OnInit} from '@angular/core';
import {ProjectReleaseStagePermissions} from '@app/core/permissions/project-release-stage.permissions';
import {NavigationService} from '@app/core/services/navigation/navigation.service';
import {ToastService} from '@app/core/services/toast/toast.service';
import {ProjectsLogicService} from '@app/logic/projects';
import {IReleaseStageMappedItem} from '@app/logic/release-stages/interfaces/i.release-stage.mapped';
import {ReleaseStagesLogicService} from '@app/logic/release-stages/release-stages.logic.service';
import {CbDialogService} from '@app/shared/components/dialog/cb-dialog.service';
import {IProjectDetailsDto, IProjectReleaseLotDto, LOT_JOB_STATUS_ENUM, MARKET_STATUS_ENUM, PROJECT_RELEASE_STAGE_TYPE_ENUM, PROJECT_RELEASE_STATUS_ENUM} from '@classictechsolutions/hubapi-transpiled-enums';
import {BehaviorSubject, finalize, Subscription, take} from 'rxjs';
import {ManageProjectReleaseStageDetailsDialogComponent} from '../details/manage-release-details-dialog/manage-release-details-dialog.component';

@Component({
    selector: 'cb-manage-project-release-stage-side-bar-card',
    templateUrl: './release-side-bar-card.component.html',
    styleUrls: ['./release-side-bar-card.component.scss'],
    standalone: false
})
export class ManageProjectReleaseStageSideBarCardComponent implements OnInit, OnDestroy {
    @Input() public mappedItem: IReleaseStageMappedItem;
    public projectDto: IProjectDetailsDto;
    public PROJECT_RELEASE_STATUS_ENUM = PROJECT_RELEASE_STATUS_ENUM;
    public PROJECT_RELEASE_STAGE_TYPE_ENUM = PROJECT_RELEASE_STAGE_TYPE_ENUM;
    public collapsed$ = new BehaviorSubject<boolean>(false);
    public hasActiveLots = false;
    public cancelValidationLoaded = false;
    public cancelInProgress = false;
    public readonly cancelReleaseDisabledReason = 'All lots must be removed or cancelled before the release can be cancelled';
    private readonly subscription$ = new Subscription();

    constructor(
        public readonly projectReleasePermissions: ProjectReleaseStagePermissions,
        private readonly projectsLogicService: ProjectsLogicService,
        private readonly releaseStagesLogicService: ReleaseStagesLogicService,
        private readonly navigationService: NavigationService,
        private readonly cbDialog: CbDialogService,
        private readonly toastService: ToastService
    ) { }

    @HostBinding('class.collapsed')
    public get isHostCollapsed(): boolean {
        return this.collapsed$.value;
    }

    public ngOnInit(): void {
        this.projectsLogicService.$getItem(this.mappedItem.projectId).pipe(take(1)).subscribe(result => {
            this.projectDto = result;
        });

        this.loadCancelValidationState();
        this.subscription$.add(this.mappedItem.$updated.subscribe(() => this.loadCancelValidationState()));
    }

    public ngOnDestroy(): void {
        this.subscription$.unsubscribe();
    }

    public viewProject(): void {
        this.navigationService.navigate([`/projects/${this.mappedItem.projectId}/details`]);
    }

    public editProjectReleaseStage() {
        const clone = this.mappedItem.$clone();
        this.cbDialog
            .open(ManageProjectReleaseStageDetailsDialogComponent, {
                data: { mappedItem: clone },
                minWidth: '80%',
            })
            .afterClosed()
            .pipe(take(1)).subscribe();
    }

    public canCancelRelease(): boolean {
        return this.projectReleasePermissions.canCancel()
            && this.cancelValidationLoaded
            && !this.hasActiveLots
            && this.mappedItem.releaseStatus !== PROJECT_RELEASE_STATUS_ENUM.Cancelled
            && !this.cancelInProgress;
    }

    public getCancelReleaseTooltip(): string {
        return this.hasActiveLots ? this.cancelReleaseDisabledReason : '';
    }

    public cancelRelease(): void {
        if (!this.canCancelRelease()) {
            return;
        }

        this.cbDialog.confirm({
            dialogHeading: 'Cancel Release',
            message: 'Are you sure you want to cancel this release?',
            confirmed: (): void => {
                const toSave = this.mappedItem.$clone();
                toSave.releaseStatus = PROJECT_RELEASE_STATUS_ENUM.Cancelled;
                this.cancelInProgress = true;

                toSave.$save().pipe(
                    take(1),
                    finalize(() => this.cancelInProgress = false),
                ).subscribe(() => {
                    this.toastService.saveSuccess();
                });
            }
        });
    }

    private loadCancelValidationState(): void {
        this.cancelValidationLoaded = false;

        this.releaseStagesLogicService.getProjectReleaseStageLots(this.mappedItem.id).pipe(take(1)).subscribe({
            next: (lots: IProjectReleaseLotDto[]) => {
                this.hasActiveLots = lots.some(this.isLotActiveForCancellation);
                this.cancelValidationLoaded = true;
            },
            error: () => {
                this.hasActiveLots = true;
                this.cancelValidationLoaded = true;
            }
        });
    }

    private readonly isLotActiveForCancellation = (lot: IProjectReleaseLotDto): boolean => {
        return lot.marketStatus !== MARKET_STATUS_ENUM.None
            || (lot.jobStatus !== LOT_JOB_STATUS_ENUM.None && lot.jobStatus !== LOT_JOB_STATUS_ENUM.Cancelled);
    };
}
